<script setup>
import ButtonSubmit from "./ButtonSubmit.vue"
import ButtonCancel from "./ButtonCancel.vue"
import ButtonDelete from "./UI/ButtonDelete.vue"
import { flashMessage } from "@/functions"
import Swal from "sweetalert2"
import api from "../api/api.js"
</script>

<template>
  <div class="body-container">
    <header>
      <h2>Banner Message</h2>
      <h2>B01</h2>
    </header>

    <p class="banner-help">
      Messages shown at the top right of Member Information (M01). Keep it to one short sentence —
      max {{ maxLength }} characters.
    </p>

    <form class="banner-form" @submit.prevent="saveBanner">
      <div class="banner-form-row">
        <label class="banner-label" for="banner-message">Message</label>
        <div class="banner-message-wrap">
          <input
            id="banner-message"
            type="text"
            class="banner-input"
            v-model="form.Message"
            :maxlength="maxLength"
            :placeholder="samplePlaceholder"
            required />
          <span class="banner-counter" :class="{ 'banner-counter-near': remaining <= 20 }">
            {{ form.Message.length }}/{{ maxLength }}
          </span>
        </div>
      </div>

      <div class="banner-form-row banner-form-grid">
        <div>
          <label class="banner-label" for="banner-severity">Type</label>
          <select id="banner-severity" class="banner-input" v-model="form.Severity">
            <option v-for="s in severities" :key="s" :value="s">{{ s }}</option>
          </select>
        </div>
        <div>
          <label class="banner-label" for="banner-start">Start (optional)</label>
          <input id="banner-start" type="datetime-local" class="banner-input" v-model="form.StartDate" />
        </div>
        <div>
          <label class="banner-label" for="banner-end">End (optional)</label>
          <input id="banner-end" type="datetime-local" class="banner-input" v-model="form.EndDate" />
        </div>
        <div class="banner-active">
          <label><input type="checkbox" v-model="form.IsActive" /> Active</label>
        </div>
      </div>

      <div class="banner-preview" v-if="form.Message.trim()">
        <span class="banner-label">Preview</span>
        <div class="m01-banner" :class="'m01-banner-' + form.Severity.toLowerCase()">
          <i class="fa-solid" :class="severityIcon(form.Severity)"></i>
          <span class="m01-banner-text">{{ form.Message }}</span>
        </div>
      </div>

      <div class="banner-actions">
        <ButtonSubmit type="submit" :label="form.IndexNum ? 'Update' : 'Save'" :disabled="!form.Message.trim() || saving" />
        <ButtonCancel type="button" label="Cancel" @click="resetForm" :disabled="saving" />
      </div>
    </form>

    <table id="table" class="banner-table">
      <thead>
        <tr>
          <th>Num</th>
          <th style="width: 45%">Message</th>
          <th>Type</th>
          <th>Schedule</th>
          <th>Status</th>
          <th>Action</th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="!banners.length">
          <td colspan="6" style="text-align: center; color: #888">No banner messages yet.</td>
        </tr>
        <tr v-for="(row, index) in banners" :key="row.IndexNum" :class="{ 'banner-row-editing': form.IndexNum === row.IndexNum }">
          <td style="text-align: right">{{ index + 1 }}</td>
          <td>{{ row.Message }}</td>
          <td>
            <span class="banner-chip" :class="'m01-banner-' + row.Severity.toLowerCase()">{{ row.Severity }}</span>
          </td>
          <td class="banner-schedule">
            <div>From: {{ row.StartDate || "Now" }}</div>
            <div>To: {{ row.EndDate || "No end" }}</div>
          </td>
          <td>{{ statusLabel(row) }}</td>
          <td>
            <div class="update-btn-td">
              <ButtonSubmit type="button" label="Edit" @click="editBanner(row)" />
              <ButtonDelete type="button" label="Delete" @click="deleteBanner(row)" />
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
const emptyForm = () => ({
  IndexNum: null,
  Message: "",
  Severity: "Info",
  StartDate: "",
  EndDate: "",
  IsActive: true,
})

export default {
  name: "BannerMessageComponent",
  props: ["brandData"],
  data: () => ({
    company: localStorage.getItem("companyID"),
    maxLength: 150,
    samplePlaceholder: "e.g. Core system maintenance tonight 10–11 PM ET. Balance lookups may be delayed.",
    severities: ["Info", "Warning", "Urgent"],
    banners: [],
    form: emptyForm(),
    saving: false,
  }),
  computed: {
    remaining() {
      return this.maxLength - this.form.Message.length
    },
  },
  mounted() {
    this.getBanners()
  },
  methods: {
    notify(text) {
      flashMessage(
        this.$flashMessage,
        this.brandData?.accent_color1 ? this.brandData.accent_color1 : "#FF7F50",
        this.brandData?.flash_text_color ? this.brandData.flash_text_color : "#ffffff",
        text
      )
    },
    severityIcon(severity) {
      if (severity === "Urgent") return "fa-circle-exclamation"
      if (severity === "Warning") return "fa-triangle-exclamation"
      return "fa-bullhorn"
    },
    statusLabel(row) {
      if (!row.IsActive) return "Inactive"
      const now = new Date()
      if (row.StartDate && new Date(row.StartDate.replace(" ", "T")) > now) return "Scheduled"
      if (row.EndDate && new Date(row.EndDate.replace(" ", "T")) < now) return "Expired"
      return "Showing"
    },
    resetForm() {
      this.form = emptyForm()
    },
    editBanner(row) {
      this.form = {
        IndexNum: row.IndexNum,
        Message: row.Message,
        Severity: row.Severity,
        StartDate: row.StartDate ? row.StartDate.replace(" ", "T") : "",
        EndDate: row.EndDate ? row.EndDate.replace(" ", "T") : "",
        IsActive: !!row.IsActive,
      }
      window.scrollTo({ top: 0, behavior: "smooth" })
    },
    async getBanners() {
      try {
        const { data } = await api.post("getBannerMessages", { Company: this.company })
        this.banners = Array.isArray(data) ? data : []
      } catch (error) {
        console.log(error)
      }
    },
    async saveBanner() {
      if (!this.form.Message.trim()) return
      this.saving = true
      const isUpdate = !!this.form.IndexNum
      const passLoginUp = JSON.parse(localStorage.getItem("passLoginUp") || "{}")
      try {
        const { data } = await api.post(isUpdate ? "updateBannerMessage" : "addBannerMessage", {
          ...this.form,
          Company: this.company,
          CreatedBy: passLoginUp.email || null,
        })
        if (data.affectedRows == 1) {
          this.resetForm()
          this.getBanners()
          this.notify(isUpdate ? "Banner message updated successfully." : "Banner message added successfully.")
        }
      } catch (error) {
        console.log(error)
        Swal.fire({ icon: "error", text: error.response?.data?.error || "Unable to save banner message." })
      } finally {
        this.saving = false
      }
    },
    deleteBanner(row) {
      Swal.fire({
        title: "",
        html: `<p>Are you sure you want to delete this banner message?</p><p><b>${row.Message.replace(/</g, "&lt;")}</b></p>`,
        icon: "question",
        showCancelButton: true,
        cancelButtonText: "No",
        confirmButtonColor: "#dc3545",
        confirmButtonText: "Yes",
      }).then(async (result) => {
        if (!result.isConfirmed) return
        try {
          const { data } = await api.post("deleteBannerMessage", { IndexNum: row.IndexNum, Company: this.company })
          if (data.affectedRows == 1) {
            if (this.form.IndexNum === row.IndexNum) this.resetForm()
            this.getBanners()
            this.notify("Banner message deleted successfully.")
          }
        } catch (error) {
          console.log(error)
        }
      })
    },
  },
}
</script>

<style scoped>
header {
  display: flex;
  justify-content: space-between;
}

.body-container {
  align-self: unset;
}

.banner-help {
  color: #555;
  margin: 0.25rem 0 1rem;
}

.banner-form {
  width: 845px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 1.5rem;
}

.banner-label {
  display: block;
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 4px;
}

.banner-input {
  line-height: 1.5;
  font-size: 1rem;
  padding: 6px 8px;
  width: 100%;
  border-radius: 10px;
  outline: none;
  border: 1px solid black;
  box-sizing: border-box;
}

.banner-message-wrap {
  position: relative;
}

.banner-message-wrap .banner-input {
  padding-right: 70px;
}

.banner-counter {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.85rem;
  color: #888;
}

.banner-counter-near {
  color: #c0392b;
  font-weight: 600;
}

.banner-form-grid {
  display: grid;
  grid-template-columns: 140px 1fr 1fr 100px;
  gap: 12px;
  align-items: end;
}

.banner-active {
  padding-bottom: 8px;
}

.banner-actions {
  display: flex;
  gap: 10px;
}

.banner-actions button {
  width: 90px;
}

.banner-table {
  width: 845px !important;
  table-layout: auto;
}

thead th {
  text-align: center !important;
}

.banner-schedule {
  font-size: 0.85rem;
  white-space: nowrap;
}

.banner-row-editing td {
  background: #fffbe6;
}

.update-btn-td {
  display: flex;
  justify-content: space-evenly;
  gap: 6px;
}

.update-btn-td button {
  width: 75px;
}

.banner-chip {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
}

/* Same look as the M01 banner so the preview is accurate */
.m01-banner {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 640px;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  border: 1px solid;
}

/* Wraps like the M01 banner, max 3 lines */
.m01-banner-text {
  white-space: normal;
  overflow-wrap: anywhere;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  overflow: hidden;
}

.m01-banner-info {
  background: #e8f1fb;
  border-color: #b6d4f2;
  color: #0e4a7a;
}

.m01-banner-warning {
  background: #fff6e0;
  border-color: #f3d48a;
  color: #7a5200;
}

.m01-banner-urgent {
  background: #fdecea;
  border-color: #f2b8b2;
  color: #a1251b;
}
</style>
